import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import {
  IconAlertCircle,
  IconBook,
  IconBrandGit,
  IconCornerLeftUp,
  IconDownload,
  IconFile,
  IconFolder,
  IconGitBranch,
  IconLock,
} from '@tabler/icons-react';
import { api, type GitCommit, type GitRepo } from '../api.js';
import { IMAGE_EXTS, basename, extOf } from '../lib/fileKinds.js';
import { formatSize, relativeTime } from '../lib/format.js';

/**
 * Self-hosted git (Forgejo) browser — read-only, GitHub-style, the git
 * sibling of pages/Lore.tsx. URL model:
 *   /git                         → repo list
 *   /git/<owner>/<repo>[/<path>] → tree
 *   ?file=<repo-relative path>   → file modal
 * Git repos hold code/tools, not UE content, so nothing here goes through
 * the dam preview pipeline: text files render inline, images inline,
 * everything else is a download.
 */

interface Loc {
  repo: string; // owner/name
  path: string; // '' at repo root
}

function parseLoc(pathname: string): Loc | null {
  const segs = pathname.replace(/^\/git\/?/, '').replace(/\/+$/, '').split('/').filter(Boolean);
  if (segs.length < 2) return null;
  return { repo: `${segs[0]}/${segs[1]}`, path: segs.slice(2).join('/') };
}

function treeUrl(repo: string, path: string): string {
  return path ? `/git/${repo}/${path}` : `/git/${repo}`;
}

// Extensions rendered as text in the modal. Files with NO extension are
// treated as text too (Makefile, Dockerfile, LICENSE…).
const TEXT_EXTS = new Set([
  'txt', 'md', 'markdown', 'rst', 'json', 'jsonc', 'yaml', 'yml', 'toml', 'ini', 'cfg', 'conf', 'env',
  'py', 'js', 'mjs', 'cjs', 'ts', 'tsx', 'jsx', 'sh', 'bash', 'zsh', 'ps1', 'bat', 'cmd',
  'c', 'h', 'cpp', 'hpp', 'cc', 'cs', 'rs', 'go', 'java', 'kt', 'swift', 'rb', 'php', 'lua',
  'html', 'htm', 'css', 'scss', 'xml', 'svg', 'sql', 'graphql', 'proto', 'csv', 'log',
  'gitignore', 'gitattributes', 'editorconfig', 'dockerfile', 'makefile', 'uplugin', 'uproject',
]);
const TEXT_PREVIEW_MAX = 512 * 1024;

export default function GitPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const loc = parseLoc(location.pathname);
  const modalFile = useMemo(
    () => new URLSearchParams(location.search).get('file'),
    [location.search],
  );

  return (
    <div className="flex flex-col gap-4">
      <ReadOnlyBanner />
      <GitBreadcrumbs loc={loc} />
      {loc === null ? <RepoList /> : <GitTree loc={loc} />}

      <Dialog
        open={modalFile !== null}
        onOpenChange={(open) => {
          if (!open) navigate(location.pathname, { replace: true });
        }}
      >
        <DialogContent className="max-w-4xl">
          {modalFile && loc && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono text-sm">{basename(modalFile)}</DialogTitle>
              </DialogHeader>
              <GitFileView repo={loc.repo} path={modalFile} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReadOnlyBanner() {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
      <IconBrandGit size={14} />
      Git — read-only browse. Clone and push with git as usual; this view follows the default branch.
    </div>
  );
}

function useRepos() {
  return useQuery({ queryKey: ['git.repos'], queryFn: api.gitRepos, retry: false, staleTime: 5 * 60_000 });
}

function GitBreadcrumbs({ loc }: { loc: Loc | null }) {
  const repos = useRepos();
  const branch = loc ? repos.data?.repos.find((r) => r.fullName === loc.repo)?.defaultBranch : undefined;
  const segs = loc?.path ? loc.path.split('/') : [];
  return (
    <nav className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
      {loc && (
        <span className="mr-1 inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs">
          <IconGitBranch size={12} />
          {branch ?? '…'}
        </span>
      )}
      <Link to="/git" className="hover:text-foreground hover:underline">
        git
      </Link>
      {loc && (
        <span className="flex items-center gap-1">
          <span>/</span>
          {segs.length === 0 ? (
            <span className="font-semibold text-foreground">{loc.repo}</span>
          ) : (
            <Link to={treeUrl(loc.repo, '')} className="font-semibold text-theia-400 hover:underline">
              {loc.repo}
            </Link>
          )}
        </span>
      )}
      {loc &&
        segs.map((seg, i) => {
          const sub = segs.slice(0, i + 1).join('/');
          const last = i === segs.length - 1;
          return (
            <span key={sub} className="flex items-center gap-1">
              <span>/</span>
              {last ? (
                <span className="text-foreground">{seg}</span>
              ) : (
                <Link to={treeUrl(loc.repo, sub)} className="text-theia-400 hover:underline">
                  {seg}
                </Link>
              )}
            </span>
          );
        })}
    </nav>
  );
}

function RepoList() {
  const q = useRepos();
  if (q.isLoading) return <Spinner />;
  if (q.error) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>Git unavailable: {(q.error as Error).message}</AlertDescription>
      </Alert>
    );
  }
  const repos: GitRepo[] = [...(q.data?.repos ?? [])].sort((a, b) =>
    a.fullName.localeCompare(b.fullName),
  );
  return (
    <div className="flex flex-col gap-2">
      {repos.length === 0 && <p className="text-sm text-muted-foreground">No git repositories yet.</p>}
      {repos.map((r) => (
        <Link
          key={r.fullName}
          to={treeUrl(r.fullName, '')}
          className="flex items-start gap-3 rounded-md border border-border bg-card px-4 py-3 hover:border-theia-600"
        >
          <IconBook size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-theia-400">{r.fullName}</span>
              {r.private && (
                <Badge variant="subtle" className="gap-1">
                  <IconLock size={10} /> private
                </Badge>
              )}
            </div>
            {r.description && <div className="mt-0.5 text-xs text-muted-foreground">{r.description}</div>}
          </div>
          {r.updatedAt && (
            <span className="shrink-0 text-xs text-muted-foreground">{relativeTime(r.updatedAt)}</span>
          )}
        </Link>
      ))}
    </div>
  );
}

function CommitBar({ commit }: { commit: GitCommit | null | undefined }) {
  if (!commit) return null;
  const author = commit.author ?? '';
  return (
    <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 text-xs">
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-theia-600/20 text-[9px] font-semibold uppercase text-theia-400"
        title={author}
      >
        {author.slice(0, 2) || '—'}
      </span>
      {author && <span className="shrink-0 font-medium text-foreground">{author}</span>}
      <span className="min-w-0 flex-1 truncate text-muted-foreground" title={commit.message}>
        {commit.message || '(no message)'}
      </span>
      <span className="shrink-0 font-mono text-[11px] text-muted-foreground" title={commit.sha}>
        {commit.sha.slice(0, 7)}
      </span>
      <span className="shrink-0 text-muted-foreground">{relativeTime(commit.time)}</span>
    </div>
  );
}

function GitTree({ loc }: { loc: Loc }) {
  const location = useLocation();
  const q = useQuery({
    queryKey: ['git.tree', loc.repo, loc.path],
    queryFn: () => api.gitTree(loc.repo, loc.path),
    retry: false,
  });
  const latest = useQuery({
    queryKey: ['git.latest', loc.repo, loc.path],
    queryFn: () => api.gitLatest(loc.repo, loc.path),
    retry: false,
    staleTime: 60_000,
  });

  if (q.isLoading) return <Spinner />;
  if (q.error) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>{(q.error as Error).message}</AlertDescription>
      </Alert>
    );
  }
  const dirs = [...(q.data?.dirs ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const files = [...(q.data?.files ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const parentUrl = loc.path ? treeUrl(loc.repo, loc.path.split('/').slice(0, -1).join('/')) : null;

  return (
    <div className="overflow-hidden rounded-md border border-border">
      <CommitBar commit={latest.data?.commit} />
      {dirs.length === 0 && files.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-muted-foreground">This directory is empty.</p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {parentUrl && (
              <tr className="border-b border-border/50 hover:bg-muted/40">
                <td colSpan={2} className="px-3 py-1.5">
                  <Link to={parentUrl} className="flex items-center gap-2 text-muted-foreground">
                    <IconCornerLeftUp size={16} className="shrink-0" />
                    <span>..</span>
                  </Link>
                </td>
              </tr>
            )}
            {dirs.map((d) => (
              <tr key={d.path} className="border-b border-border/50 last:border-0 hover:bg-muted/40">
                <td className="px-3 py-1.5">
                  <Link
                    to={treeUrl(loc.repo, d.path)}
                    className="flex items-center gap-2 hover:text-theia-400 hover:underline"
                  >
                    <IconFolder size={16} className="shrink-0 fill-theia-400/20 text-theia-400" />
                    <span>{d.name}</span>
                  </Link>
                </td>
                <td className="w-28 px-3 py-1.5" />
              </tr>
            ))}
            {files.map((f) => (
              <tr key={f.path} className="border-b border-border/50 last:border-0 hover:bg-muted/40">
                <td className="px-3 py-1.5">
                  <Link
                    to={{ pathname: location.pathname, search: `?file=${encodeURIComponent(f.path)}` }}
                    className="flex items-center gap-2 hover:text-theia-400 hover:underline"
                  >
                    <IconFile size={16} className="shrink-0 text-muted-foreground" />
                    <span>{f.name}</span>
                  </Link>
                </td>
                <td className="w-28 px-3 py-1.5 text-right text-xs tabular-nums text-muted-foreground">
                  {formatSize(f.size)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function isTextName(name: string): boolean {
  const ext = extOf(name);
  return ext === '' || TEXT_EXTS.has(ext) || TEXT_EXTS.has(name.toLowerCase());
}

function GitFileView({ repo, path }: { repo: string; path: string }) {
  const name = basename(path);
  const url = api.gitFileUrl(repo, path);
  const isImage = IMAGE_EXTS.has(extOf(name));
  const isText = !isImage && isTextName(name);

  const text = useQuery({
    queryKey: ['git.file.text', repo, path],
    enabled: isText,
    retry: false,
    queryFn: async () => {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const body = await res.text();
      return body.length > TEXT_PREVIEW_MAX
        ? `${body.slice(0, TEXT_PREVIEW_MAX)}\n\n… truncated at ${formatSize(TEXT_PREVIEW_MAX)}; download for the full file.`
        : body;
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="font-mono">{path}</span>
        <a href={url} download={name} className="flex items-center gap-1 text-theia-400 hover:underline">
          <IconDownload size={14} /> download
        </a>
      </div>
      {isImage ? (
        <img src={url} alt={name} className="max-h-[70vh] max-w-full self-start rounded border border-border" />
      ) : isText ? (
        text.isLoading ? (
          <Spinner />
        ) : text.error ? (
          <Alert tone="destructive">
            <IconAlertCircle />
            <AlertDescription>{(text.error as Error).message}</AlertDescription>
          </Alert>
        ) : (
          <pre className="max-h-[70vh] overflow-auto rounded bg-muted/40 px-3 py-2 font-mono text-[11px] leading-relaxed">
            {text.data}
          </pre>
        )
      ) : (
        <p className="text-sm text-muted-foreground">No inline preview for this file type — use the download link above.</p>
      )}
    </div>
  );
}
